import { useCallback, useEffect, useMemo, useState } from "react";

import {
  DEFAULT_ORDERS_WIDGET_VISIBLE_COLUMN_IDS,
  getOrdersWidgetAvailableColumnIds,
  isOrdersWidgetColumnId,
  type OrdersWidgetColumnId,
} from "./OrdersWidget.columns";

export const ORDERS_COLUMNS_STORAGE_KEY = "atd:orders:columns:v7";
export const ORDERS_COLUMNS_WITH_ACTIONS_STORAGE_KEY =
  "atd:orders:columns:v7:actions";
export const ORDERS_COLUMNS_LEGACY_STORAGE_KEY = "atd:orders:columns:v6";
export const ORDERS_COLUMNS_WITH_ACTIONS_LEGACY_STORAGE_KEY =
  "atd:orders:columns:v6:actions";

export type OrdersColumnMoveDirection = "up" | "down";
export type OrdersColumnDropDirection = "before" | "after";

export type ReorderOrdersColumns = (
  sourceColumnId: OrdersWidgetColumnId,
  targetColumnId: OrdersWidgetColumnId,
  direction: OrdersColumnDropDirection,
) => void;

export interface OrdersColumnPreference {
  order: OrdersWidgetColumnId[];
  visible: OrdersWidgetColumnId[];
}

export interface OrdersColumnVisibilityStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

const ACTION_COLUMN_ID: OrdersWidgetColumnId = "action";
const SYMBOL_COLUMN_ID: OrdersWidgetColumnId = "symbol";
const NAME_COLUMN_ID: OrdersWidgetColumnId = "name";
const SUBMITTED_AT_COLUMN_ID: OrdersWidgetColumnId = "submittedAt";
const RETIRED_TIMESTAMP_COLUMN_IDS = new Set([
  "clockTime",
  "orderTime",
  "orderDate",
]);

const migrateLegacyTimestampColumnId = (value: unknown): unknown =>
  typeof value === "string" && RETIRED_TIMESTAMP_COLUMN_IDS.has(value)
    ? SUBMITTED_AT_COLUMN_ID
    : value;

const migrateLegacyTimestampColumnIds = (
  values: readonly unknown[],
): unknown[] => values.map(migrateLegacyTimestampColumnId);

const getDefaultColumnIds = (
  includeAction: boolean = false,
): OrdersWidgetColumnId[] =>
  includeAction
    ? [...DEFAULT_ORDERS_WIDGET_VISIBLE_COLUMN_IDS, ACTION_COLUMN_ID]
    : [...DEFAULT_ORDERS_WIDGET_VISIBLE_COLUMN_IDS];

const getStorageKey = (includeAction: boolean): string =>
  includeAction
    ? ORDERS_COLUMNS_WITH_ACTIONS_STORAGE_KEY
    : ORDERS_COLUMNS_STORAGE_KEY;

const getLegacyStorageKey = (includeAction: boolean): string =>
  includeAction
    ? ORDERS_COLUMNS_WITH_ACTIONS_LEGACY_STORAGE_KEY
    : ORDERS_COLUMNS_LEGACY_STORAGE_KEY;

const isRequiredColumn = (
  columnId: OrdersWidgetColumnId,
  includeAction: boolean,
): boolean =>
  columnId === SYMBOL_COLUMN_ID ||
  (includeAction && columnId === ACTION_COLUMN_ID);

const getOrdersColumnStorage = (): OrdersColumnVisibilityStorage | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const sameColumnIds = (
  left: readonly OrdersWidgetColumnId[],
  right: readonly OrdersWidgetColumnId[],
): boolean =>
  left.length === right.length &&
  left.every((id, index) => id === right[index]);

const samePreference = (
  left: OrdersColumnPreference,
  right: OrdersColumnPreference,
): boolean =>
  sameColumnIds(left.order, right.order) &&
  sameColumnIds(left.visible, right.visible);

const withoutPinnedColumns = (
  columnIds: readonly OrdersWidgetColumnId[],
): OrdersWidgetColumnId[] =>
  columnIds.filter(
    (columnId) =>
      columnId !== ACTION_COLUMN_ID && columnId !== SYMBOL_COLUMN_ID,
  );

const withPinnedColumns = (
  columnIds: readonly OrdersWidgetColumnId[],
  includeAction: boolean,
): OrdersWidgetColumnId[] => {
  const middle = withoutPinnedColumns(columnIds);
  return includeAction
    ? [SYMBOL_COLUMN_ID, ...middle, ACTION_COLUMN_ID]
    : [SYMBOL_COLUMN_ID, ...middle];
};

const collectKnownColumnIds = (
  value: readonly unknown[],
): OrdersWidgetColumnId[] => {
  const seen = new Set<OrdersWidgetColumnId>();
  const columnIds: OrdersWidgetColumnId[] = [];

  for (const candidate of value) {
    if (!isOrdersWidgetColumnId(candidate) || seen.has(candidate)) {
      continue;
    }
    seen.add(candidate);
    columnIds.push(candidate);
  }

  return columnIds;
};

export const normalizeOrdersColumnOrder = (
  requestedOrder: readonly unknown[],
  availableColumnIds: readonly OrdersWidgetColumnId[],
  includeAction: boolean,
): OrdersWidgetColumnId[] => {
  const availableSet = new Set(availableColumnIds);
  const seen = new Set<OrdersWidgetColumnId>();
  const ordered: OrdersWidgetColumnId[] = [];

  for (const columnId of collectKnownColumnIds(requestedOrder)) {
    if (
      columnId === ACTION_COLUMN_ID ||
      columnId === SYMBOL_COLUMN_ID ||
      !availableSet.has(columnId)
    ) {
      continue;
    }
    seen.add(columnId);
    ordered.push(columnId);
  }

  if (availableSet.has(NAME_COLUMN_ID) && !seen.has(NAME_COLUMN_ID)) {
    ordered.unshift(NAME_COLUMN_ID);
    seen.add(NAME_COLUMN_ID);
  }

  for (const columnId of availableColumnIds) {
    if (
      columnId === ACTION_COLUMN_ID ||
      columnId === SYMBOL_COLUMN_ID ||
      seen.has(columnId)
    ) {
      continue;
    }
    ordered.push(columnId);
  }

  return withPinnedColumns(
    ordered,
    includeAction && availableSet.has(ACTION_COLUMN_ID),
  );
};

export const normalizeOrdersVisibleColumnIds = (
  requestedVisible: readonly unknown[],
  columnOrder: readonly OrdersWidgetColumnId[],
  includeAction: boolean,
): OrdersWidgetColumnId[] => {
  const orderSet = new Set(columnOrder);
  const requestedKnown = collectKnownColumnIds(requestedVisible).filter(
    (columnId) => orderSet.has(columnId),
  );

  if (requestedKnown.length === 0) {
    const defaults = new Set(getDefaultColumnIds(includeAction));
    return columnOrder.filter((columnId) => defaults.has(columnId));
  }

  const visible = new Set(requestedKnown);
  if (orderSet.has(SYMBOL_COLUMN_ID)) {
    visible.add(SYMBOL_COLUMN_ID);
  }
  if (includeAction && orderSet.has(ACTION_COLUMN_ID)) {
    visible.add(ACTION_COLUMN_ID);
  }

  return columnOrder.filter((columnId) => visible.has(columnId));
};

export const clampOrdersColumnPreference = (
  preference: {
    order?: readonly unknown[];
    visible?: readonly unknown[];
  },
  availableColumnIds: readonly OrdersWidgetColumnId[],
  includeActionByDefault: boolean = false,
): OrdersColumnPreference => {
  const requestedOrder = migrateLegacyTimestampColumnIds(
    preference.order ?? [],
  );
  const storedHadName =
    collectKnownColumnIds(requestedOrder).includes(NAME_COLUMN_ID);
  const order = normalizeOrdersColumnOrder(
    requestedOrder,
    availableColumnIds,
    includeActionByDefault,
  );
  const requestedVisible = migrateLegacyTimestampColumnIds(
    preference.visible ?? [],
  );
  const visible = normalizeOrdersVisibleColumnIds(
    storedHadName || collectKnownColumnIds(requestedVisible).length === 0
      ? requestedVisible
      : [NAME_COLUMN_ID, ...requestedVisible],
    order,
    includeActionByDefault,
  );

  return { order, visible };
};

export const clampOrdersColumnVisibility = (
  visibleColumnIds: readonly OrdersWidgetColumnId[],
  availableColumnIds: readonly OrdersWidgetColumnId[],
  includeActionByDefault: boolean = false,
): OrdersWidgetColumnId[] =>
  clampOrdersColumnPreference(
    { order: visibleColumnIds, visible: visibleColumnIds },
    availableColumnIds,
    includeActionByDefault,
  ).visible;

export const sanitizeOrdersColumnVisibility = (
  value: unknown,
  includeActionByDefault: boolean = false,
  allowAdvancedInstructions: boolean = true,
): OrdersWidgetColumnId[] =>
  sanitizeOrdersColumnPreference(
    value,
    includeActionByDefault,
    allowAdvancedInstructions,
  ).visible;

const parseStoredPreference = (
  value: unknown,
): {
  order: unknown[];
  visible: unknown[];
} => {
  if (Array.isArray(value)) {
    return { order: [], visible: value };
  }

  if (value !== null && typeof value === "object") {
    const record = value as { order?: unknown; visible?: unknown };
    const visible = Array.isArray(record.visible) ? record.visible : [];
    const order = Array.isArray(record.order) ? record.order : visible;
    return { order, visible };
  }

  return { order: [], visible: [] };
};

export const sanitizeOrdersColumnPreference = (
  value: unknown,
  includeActionByDefault: boolean = false,
  allowAdvancedInstructions: boolean = true,
): OrdersColumnPreference => {
  const availableColumnIds = getOrdersWidgetAvailableColumnIds(
    includeActionByDefault,
    allowAdvancedInstructions,
  );

  if (value === null || value === undefined) {
    return clampOrdersColumnPreference(
      {
        order: availableColumnIds,
        visible: getDefaultColumnIds(includeActionByDefault),
      },
      availableColumnIds,
      includeActionByDefault,
    );
  }

  return clampOrdersColumnPreference(
    parseStoredPreference(value),
    availableColumnIds,
    includeActionByDefault,
  );
};

const readStoredValue = (
  storage: OrdersColumnVisibilityStorage,
  includeActionByDefault: boolean,
): string | null => {
  const storedValue = storage.getItem(getStorageKey(includeActionByDefault));
  if (storedValue !== null) {
    return storedValue;
  }

  return storage.getItem(getLegacyStorageKey(includeActionByDefault));
};

export const loadOrdersColumnPreference = (
  storage: OrdersColumnVisibilityStorage | null = getOrdersColumnStorage(),
  includeActionByDefault: boolean = false,
  allowAdvancedInstructions: boolean = true,
): OrdersColumnPreference => {
  if (!storage) {
    return sanitizeOrdersColumnPreference(
      null,
      includeActionByDefault,
      allowAdvancedInstructions,
    );
  }

  try {
    const storedValue = readStoredValue(storage, includeActionByDefault);
    return storedValue === null
      ? sanitizeOrdersColumnPreference(
          null,
          includeActionByDefault,
          allowAdvancedInstructions,
        )
      : sanitizeOrdersColumnPreference(
          JSON.parse(storedValue),
          includeActionByDefault,
          allowAdvancedInstructions,
        );
  } catch {
    return sanitizeOrdersColumnPreference(
      null,
      includeActionByDefault,
      allowAdvancedInstructions,
    );
  }
};

export const loadOrdersColumnVisibility = (
  storage: OrdersColumnVisibilityStorage | null = getOrdersColumnStorage(),
  includeActionByDefault: boolean = false,
  allowAdvancedInstructions: boolean = true,
): OrdersWidgetColumnId[] =>
  loadOrdersColumnPreference(
    storage,
    includeActionByDefault,
    allowAdvancedInstructions,
  ).visible;

export const persistOrdersColumnPreference = (
  preference: OrdersColumnPreference,
  storage: OrdersColumnVisibilityStorage | null = getOrdersColumnStorage(),
  includeActionByDefault: boolean = false,
  allowAdvancedInstructions: boolean = true,
): boolean => {
  if (!storage) {
    return false;
  }

  try {
    const sanitized = sanitizeOrdersColumnPreference(
      preference,
      includeActionByDefault,
      allowAdvancedInstructions,
    );
    storage.setItem(
      getStorageKey(includeActionByDefault),
      JSON.stringify(sanitized),
    );
    return true;
  } catch {
    return false;
  }
};

export const persistOrdersColumnVisibility = (
  visibleColumnIds: readonly OrdersWidgetColumnId[],
  storage: OrdersColumnVisibilityStorage | null = getOrdersColumnStorage(),
  includeActionByDefault: boolean = false,
  allowAdvancedInstructions: boolean = true,
): boolean =>
  persistOrdersColumnPreference(
    { order: [], visible: [...visibleColumnIds] },
    storage,
    includeActionByDefault,
    allowAdvancedInstructions,
  );

export const moveOrdersColumn = (
  columnOrder: readonly OrdersWidgetColumnId[],
  columnId: OrdersWidgetColumnId,
  direction: OrdersColumnMoveDirection,
): OrdersWidgetColumnId[] => {
  if (columnId === ACTION_COLUMN_ID || columnId === SYMBOL_COLUMN_ID) {
    return [...columnOrder];
  }

  const movable = withoutPinnedColumns(columnOrder);
  const index = movable.indexOf(columnId);
  if (index < 0) {
    return [...columnOrder];
  }

  const targetIndex = direction === "up" ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= movable.length) {
    return [...columnOrder];
  }

  const next = [...movable];
  const [removed] = next.splice(index, 1);
  next.splice(targetIndex, 0, removed);

  return withPinnedColumns(next, columnOrder.includes(ACTION_COLUMN_ID));
};

export const reorderOrdersColumn = (
  columnOrder: readonly OrdersWidgetColumnId[],
  sourceColumnId: OrdersWidgetColumnId,
  targetColumnId: OrdersWidgetColumnId,
  direction: OrdersColumnDropDirection,
): OrdersWidgetColumnId[] => {
  if (
    sourceColumnId === ACTION_COLUMN_ID ||
    targetColumnId === ACTION_COLUMN_ID ||
    sourceColumnId === SYMBOL_COLUMN_ID ||
    targetColumnId === SYMBOL_COLUMN_ID ||
    sourceColumnId === targetColumnId ||
    (direction !== "before" && direction !== "after")
  ) {
    return [...columnOrder];
  }

  const movable = withoutPinnedColumns(columnOrder);
  const fromIndex = movable.indexOf(sourceColumnId);
  const toIndex = movable.indexOf(targetColumnId);
  if (fromIndex < 0 || toIndex < 0) {
    return [...columnOrder];
  }

  const next = [...movable];
  const [removed] = next.splice(fromIndex, 1);
  const targetIndex = next.indexOf(targetColumnId);
  if (targetIndex < 0) {
    return [...columnOrder];
  }

  next.splice(
    direction === "after" ? targetIndex + 1 : targetIndex,
    0,
    removed,
  );

  return withPinnedColumns(next, columnOrder.includes(ACTION_COLUMN_ID));
};

const applyOrder = (
  preference: OrdersColumnPreference,
  nextOrder: readonly OrdersWidgetColumnId[],
): OrdersColumnPreference => {
  const visibleSet = new Set(preference.visible);
  return {
    order: [...nextOrder],
    visible: nextOrder.filter((columnId) => visibleSet.has(columnId)),
  };
};

export const useOrdersColumnVisibility = (
  storage: OrdersColumnVisibilityStorage | null = getOrdersColumnStorage(),
  includeActionByDefault: boolean = false,
  allowAdvancedInstructions: boolean = true,
) => {
  const availableColumnIds = useMemo(
    () =>
      getOrdersWidgetAvailableColumnIds(
        includeActionByDefault,
        allowAdvancedInstructions,
      ),
    [allowAdvancedInstructions, includeActionByDefault],
  );

  const [preference, setPreference] = useState<OrdersColumnPreference>(() =>
    loadOrdersColumnPreference(
      storage,
      includeActionByDefault,
      allowAdvancedInstructions,
    ),
  );

  const effectivePreference = useMemo(
    () =>
      clampOrdersColumnPreference(
        preference,
        availableColumnIds,
        includeActionByDefault,
      ),
    [availableColumnIds, includeActionByDefault, preference],
  );

  useEffect(() => {
    setPreference((current) => {
      const next = clampOrdersColumnPreference(
        current,
        availableColumnIds,
        includeActionByDefault,
      );
      return samePreference(current, next) ? current : next;
    });
  }, [availableColumnIds, includeActionByDefault]);

  useEffect(() => {
    persistOrdersColumnPreference(
      effectivePreference,
      storage,
      includeActionByDefault,
      allowAdvancedInstructions,
    );
  }, [
    allowAdvancedInstructions,
    effectivePreference,
    includeActionByDefault,
    storage,
  ]);

  const setColumnVisibility = useCallback(
    (columnId: OrdersWidgetColumnId, isVisible: boolean) => {
      if (!availableColumnIds.includes(columnId)) {
        return;
      }

      setPreference((currentPreference) => {
        const current = clampOrdersColumnPreference(
          currentPreference,
          availableColumnIds,
          includeActionByDefault,
        );
        const isCurrentlyVisible = current.visible.includes(columnId);
        if (isCurrentlyVisible === isVisible) {
          return currentPreference;
        }
        if (!isVisible && isRequiredColumn(columnId, includeActionByDefault)) {
          return currentPreference;
        }
        if (!isVisible && current.visible.length === 1) {
          return currentPreference;
        }

        const nextVisible = new Set(current.visible);
        if (isVisible) {
          nextVisible.add(columnId);
        } else {
          nextVisible.delete(columnId);
        }

        return {
          order: current.order,
          visible: current.order.filter((id) => nextVisible.has(id)),
        };
      });
    },
    [availableColumnIds, includeActionByDefault],
  );

  const moveColumn = useCallback(
    (columnId: OrdersWidgetColumnId, direction: OrdersColumnMoveDirection) => {
      setPreference((currentPreference) => {
        const current = clampOrdersColumnPreference(
          currentPreference,
          availableColumnIds,
          includeActionByDefault,
        );
        const nextOrder = moveOrdersColumn(current.order, columnId, direction);
        const next = applyOrder(current, nextOrder);
        return samePreference(current, next) ? currentPreference : next;
      });
    },
    [availableColumnIds, includeActionByDefault],
  );

  const reorderColumns = useCallback<ReorderOrdersColumns>(
    (sourceColumnId, targetColumnId, direction) => {
      if (
        !isOrdersWidgetColumnId(sourceColumnId) ||
        !isOrdersWidgetColumnId(targetColumnId)
      ) {
        return;
      }

      setPreference((currentPreference) => {
        const current = clampOrdersColumnPreference(
          currentPreference,
          availableColumnIds,
          includeActionByDefault,
        );
        const nextOrder = reorderOrdersColumn(
          current.order,
          sourceColumnId,
          targetColumnId,
          direction,
        );
        const next = applyOrder(current, nextOrder);
        return samePreference(current, next) ? currentPreference : next;
      });
    },
    [availableColumnIds, includeActionByDefault],
  );

  const selectAllColumns = useCallback(() => {
    setPreference((currentPreference) => {
      const current = clampOrdersColumnPreference(
        currentPreference,
        availableColumnIds,
        includeActionByDefault,
      );
      return {
        order: current.order,
        visible: [...current.order],
      };
    });
  }, [availableColumnIds, includeActionByDefault]);

  const resetColumnVisibility = useCallback(() => {
    setPreference(
      clampOrdersColumnPreference(
        {
          order: availableColumnIds,
          visible: getDefaultColumnIds(includeActionByDefault),
        },
        availableColumnIds,
        includeActionByDefault,
      ),
    );
  }, [availableColumnIds, includeActionByDefault]);

  return {
    columnOrder: effectivePreference.order,
    visibleColumnIds: effectivePreference.visible,
    setColumnVisibility,
    moveColumn,
    reorderColumns,
    selectAllColumns,
    resetColumnVisibility,
  };
};
